import React, { useState, useEffect, useRef } from 'react';
import { csrfFetch } from '../../utils/csrfFetch';

/**
 * ProjectSettingsModal - Modal for editing project settings including custom instructions (SAT-64)
 * Updated with Knowledge Base tab for document uploads (SAT-103)
 *
 * Features:
 * - Edit project name, description, color
 * - Custom instructions editor with character limit
 * - Preview of how instructions will be applied
 * - Knowledge base document upload
 * - Save/Cancel actions
 */

const MAX_INSTRUCTIONS_LENGTH = 4000;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_EXTENSIONS = ['.pdf', '.txt', '.md', '.docx'];

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (filename) => {
  const ext = filename.split('.').pop().toLowerCase();
  switch (ext) {
    case 'pdf':
      return (
        <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
        </svg>
      );
    case 'txt':
    case 'md':
      return (
        <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
        </svg>
      );
    case 'docx':
      return (
        <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
        </svg>
      );
    default:
      return (
        <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
        </svg>
      );
  }
};

const COLOR_OPTIONS = [
  { name: 'Brand',  value: '#005d7e' },  // Decisiv primary
  { name: 'Rust',   value: '#e89213' },  // Decisiv accent
  { name: 'Blue',   value: '#3B82F6' },
  { name: 'Green',  value: '#10B981' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Amber',  value: '#F59E0B' },
  { name: 'Red',    value: '#EF4444' },
  { name: 'Pink',   value: '#EC4899' },
  { name: 'Cyan',   value: '#06B6D4' },
];

function ProjectSettingsModal({ project, isOpen, onClose, onSave }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#005d7e');
  const [customInstructions, setCustomInstructions] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('general');
  const [showPreview, setShowPreview] = useState(false);

  // Knowledge base state (SAT-103)
  const [documents, setDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  // Load project data when modal opens
  useEffect(() => {
    if (project && isOpen) {
      setName(project.name || '');
      setDescription(project.description || '');
      setColor(project.color || '#005d7e');
      setCustomInstructions(project.custom_instructions || '');
      setError('');
      setUploadError('');
      setActiveTab('general');
      // Fetch knowledge base documents
      fetchDocuments();
    }
  }, [project, isOpen]);

  // Fetch documents from the knowledge base (SAT-103)
  const fetchDocuments = async () => {
    if (!project?.id) return;

    try {
      const response = await csrfFetch(`/api/projects/${project.id}/knowledge`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    }
  };

  // Handle file upload (SAT-103)
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    const ext = '.' + file.name.split('.').pop().toLowerCase();

    // Validate file extension
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setUploadError(`Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(`File too large. Maximum size: ${formatFileSize(MAX_FILE_SIZE)}`);
      return;
    }

    setIsUploading(true);
    setUploadError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await csrfFetch(`/api/projects/${project.id}/knowledge`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to upload document');
      }

      // Refresh document list
      await fetchDocuments();
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle document deletion (SAT-103)
  const handleDeleteDocument = async (filename) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await csrfFetch(`/api/projects/${project.id}/knowledge/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to delete document');
      }

      // Refresh document list
      await fetchDocuments();
    } catch (err) {
      setUploadError(err.message);
    }
  };

  // Handle drag and drop (SAT-103)
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    handleFileUpload(files);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Project name is required');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const response = await csrfFetch(`/api/projects/${project.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim() || null,
          color,
          custom_instructions: customInstructions.trim() || null
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update project');
      }

      const data = await response.json();
      if (onSave) {
        onSave(data.project);
      }
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInstructionsChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_INSTRUCTIONS_LENGTH) {
      setCustomInstructions(value);
    }
  };

  if (!isOpen) return null;

  const instructionsLength = customInstructions.length;
  const instructionsPercentage = (instructionsLength / MAX_INSTRUCTIONS_LENGTH) * 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Project Settings
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 px-6">
          <button
            onClick={() => setActiveTab('general')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'general'
                ? 'border-decisiv-blue text-decisiv-blue'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            General
          </button>
          <button
            onClick={() => setActiveTab('instructions')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'instructions'
                ? 'border-decisiv-blue text-decisiv-blue'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Custom Instructions
          </button>
          <button
            onClick={() => setActiveTab('knowledge')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'knowledge'
                ? 'border-decisiv-blue text-decisiv-blue'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Knowledge Base
            {documents.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-gray-200 dark:bg-gray-600 rounded-full">
                {documents.length}
              </span>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {activeTab === 'general' && (
            <div className="space-y-6">
              {/* Project Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Project Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter project name"
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg
                    text-gray-900 dark:text-white placeholder-gray-400
                    focus:outline-hidden focus:ring-2 focus:ring-decisiv-blue focus:border-transparent"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what this project is about..."
                  rows={3}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg
                    text-gray-900 dark:text-white placeholder-gray-400 resize-none
                    focus:outline-hidden focus:ring-2 focus:ring-decisiv-blue focus:border-transparent"
                />
              </div>

              {/* Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Color
                </label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setColor(option.value)}
                      className={`w-8 h-8 rounded-full transition-all ${
                        color === option.value
                          ? 'ring-2 ring-offset-2 ring-gray-400 dark:ring-offset-gray-800'
                          : 'hover:scale-110'
                      }`}
                      style={{ backgroundColor: option.value }}
                      title={option.name}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'instructions' && (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Custom Instructions
                  </label>
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className="text-sm text-decisiv-blue hover:underline"
                  >
                    {showPreview ? 'Hide Preview' : 'Show Preview'}
                  </button>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  These instructions will be prepended to the system prompt for all conversations in this project.
                </p>
                <textarea
                  value={customInstructions}
                  onChange={handleInstructionsChange}
                  placeholder="Example: You are a helpful coding assistant specializing in React and TypeScript. Always provide type-safe code examples and explain your reasoning."
                  rows={8}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg
                    text-gray-900 dark:text-white placeholder-gray-400 resize-none font-mono text-sm
                    focus:outline-hidden focus:ring-2 focus:ring-decisiv-blue focus:border-transparent"
                />

                {/* Character counter */}
                <div className="mt-2 flex items-center justify-between">
                  <div className="flex-1 mr-4">
                    <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          instructionsPercentage > 90
                            ? 'bg-red-500'
                            : instructionsPercentage > 75
                            ? 'bg-yellow-500'
                            : 'bg-decisiv-blue'
                        }`}
                        style={{ width: `${Math.min(instructionsPercentage, 100)}%` }}
                      />
                    </div>
                  </div>
                  <span className={`text-sm ${
                    instructionsPercentage > 90
                      ? 'text-red-500'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {instructionsLength.toLocaleString()} / {MAX_INSTRUCTIONS_LENGTH.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Preview */}
              {showPreview && customInstructions.trim() && (
                <div className="mt-4 p-4 bg-gray-100 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Preview: How the AI will see the instructions
                  </h4>
                  <div className="p-3 bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-600">
                    <p className="text-sm text-gray-600 dark:text-gray-400 font-mono whitespace-pre-wrap">
                      <span className="text-decisiv-blue">[Project Instructions]</span>
                      {'\n'}{customInstructions}
                      {'\n\n'}
                      <span className="text-gray-400">[Default system prompt follows...]</span>
                    </p>
                  </div>
                </div>
              )}

              {/* Tips */}
              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
                  Tips for effective instructions
                </h4>
                <ul className="text-sm text-blue-600 dark:text-blue-400 space-y-1">
                  <li>• Be specific about the domain or expertise you need</li>
                  <li>• Mention preferred coding languages, frameworks, or styles</li>
                  <li>• Define the tone: formal, casual, technical, etc.</li>
                  <li>• Specify output format preferences (e.g., always include examples)</li>
                </ul>
              </div>
            </div>
          )}

          {/* Knowledge Base Tab (SAT-103) */}
          {activeTab === 'knowledge' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Project Documents
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Upload documents to include as context for all conversations in this project.
                  DP will be able to reference these documents when answering questions.
                </p>

                {/* Upload Error */}
                {uploadError && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
                    {uploadError}
                  </div>
                )}

                {/* Upload Area */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isDragOver
                      ? 'border-decisiv-blue bg-orange-50 dark:bg-orange-900/10'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={(e) => handleFileUpload(e.target.files)}
                    accept={ALLOWED_EXTENSIONS.join(',')}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={isUploading}
                  />

                  {isUploading ? (
                    <div className="flex flex-col items-center">
                      <svg className="animate-spin h-8 w-8 text-decisiv-blue mb-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Uploading...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <svg className="w-10 h-10 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Drop files here or click to upload
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Supported: {ALLOWED_EXTENSIONS.join(', ')} (Max {formatFileSize(MAX_FILE_SIZE)})
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Document List */}
              {documents.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Uploaded Documents ({documents.length})
                  </h4>
                  <div className="space-y-2">
                    {documents.map((doc, index) => (
                      <div
                        key={doc.filename || index}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg group"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {getFileIcon(doc.originalName || doc.filename)}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {doc.originalName || doc.filename}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatFileSize(doc.size)} • {new Date(doc.uploadedAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteDocument(doc.filename)}
                          className="p-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                          title="Delete document"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {documents.length === 0 && (
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg text-center">
                  <svg className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No documents uploaded yet
                  </p>
                </div>
              )}

              {/* Info Box */}
              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
                  How knowledge base works
                </h4>
                <ul className="text-sm text-blue-600 dark:text-blue-400 space-y-1">
                  <li>• Documents are included in the context for all conversations</li>
                  <li>• DP can reference and cite information from these files</li>
                  <li>• Text files (.txt, .md) work best for accurate referencing</li>
                  <li>• PDF/DOCX support may have limited text extraction</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-850">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium bg-decisiv-blue text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProjectSettingsModal;
